import { Component, Input, OnInit, Output, EventEmitter, forwardRef } from '@angular/core';
import { AdresseEntite } from '../entites/adresse';
import { DialogService } from 'primeng/dynamicdialog';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EmplacementService } from '../services/emplacement-service';
import { Emplacement } from '../entites/emplacement';
import { ReponseBase } from '../services/reponse-base';
import { MessageService, SelectItem } from 'primeng/api';
import { EmplacementAdresseEntite } from '../entites/emplacement-adresse-entite';
import { EStatuts } from '../entites/enums/statuts';
import { AjouterAdresseEmplacementComponent } from '../pages/emplacement/controles/ajouter-adresse-emplacement/ajouter-adresse-emplacement.component';

interface SelectItemGroup {
    label: string;
    value?: any;
    data: any;
    items: SelectItem[];
}

/**
 * Component for selecting an address for an emplacement
 * Implements ControlValueAccessor to work with ngModel
 */
@Component({
    selector: 'app-selection-adresse-emplacement',
    templateUrl: './selection-adresse-emplacement.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectionAdresseEmplacementComponent),
            multi: true
        }
    ],
    styles: [`
        :host ::ng-deep .p-dropdown-item-group {
            height: 0 !important;   
            padding: 0 !important;
        }
    `]
})
export class SelectionAdresseEmplacementComponent implements OnInit, ControlValueAccessor {

    @Input() emplacementId: number;
    emplacementOptions: SelectItemGroup[] = [];
    selectedAdresse: AdresseEntite;
    @Output() emplacementIdChange = new EventEmitter<number>();

    onChange: any = () => { };
    onTouched: any = () => { };
    
    private optionsLoaded: boolean = false;
    private pendingValue: any = null;

    constructor(private dialogService: DialogService,
        private emplacementService: EmplacementService,
        private messageService: MessageService) { }

    ngOnInit() {
        this.emplacementService.Obtenir().then((emplacements: ReponseBase<Emplacement[]>) => {
            this.emplacementOptions = emplacements.valeur.filter(x => x.statut.statutId != EStatuts.Archive).map((emplacement: Emplacement) => ({
                label: emplacement.nom + ' (' + emplacement.code + ')',
                value: emplacement.emplacementId,
                data: emplacement.code,
                items: [{
                    title: `${emplacement.nom} (${emplacement.code})`,
                    label: `${emplacement.nom} ${emplacement.adresse ? '(' + this.printAdresse(emplacement.adresse) + ')' : ''}`,
                    value: emplacement.adresse,
                    icon: 'pi pi-map-marker',
                    type: 'principal',
                    emplacementId: emplacement.emplacementId
                }, ...emplacement.emplacementAdresses.map((adresse: EmplacementAdresseEntite) => ({
                    title: `${emplacement.nom} (${emplacement.code})`,
                    label: `${adresse.adresse.nom} ${(this.adressDifferent(emplacement.adresse, adresse.adresse) ? '(' + this.printAdresse(adresse.adresse) + ')' : '')}`,
                    icon: 'pi pi-map-marker',
                    value: adresse.adresse,
                    type: 'adresse',
                    emplacementId: emplacement.emplacementId
                }))]
            }));

            this.optionsLoaded = true;

            // Process any pending value that was set before options were loaded
            if (this.pendingValue) {
                this.writeValue(this.pendingValue);
                this.pendingValue = null;
            } else if (this.emplacementId && !this.selectedAdresse) {
                this.selectedAdresse = this.emplacementOptions.find(x => x.value == this.emplacementId).items.find(x => x['emplacementId'] == this.emplacementId && (x as any).type == "principal")?.value;
            }
        });
    }

    writeValue(value: any): void {
        if (value) {
            // If options aren't loaded yet, queue the value for later processing
            if (!this.optionsLoaded) {
                this.pendingValue = value;
                return;
            }

            // Find the actual item from options to ensure reference equality for dropdown
            const emplacementGroup = this.emplacementOptions.find(x => x.items.some(y => y.value?.adresseId == value.adresseId));
            if (emplacementGroup) {
                const matchingItem = emplacementGroup.items.find(y => y.value?.adresseId == value.adresseId);
                if (matchingItem) {
                    this.selectedAdresse = matchingItem.value;
                    this.emplacementId = emplacementGroup.value;
                    this.emplacementIdChange.emit(this.emplacementId);
                }
            }
        }
    }

    change(value: any): void {
        this.onChange(value);
        if (value.emplacementId == undefined) {
            value.emplacementId = this.emplacementOptions.find(x => x.items.some(y => y.value?.adresseId == value.adresseId)).value;
        }
        this.emplacementIdChange.emit(value?.emplacementId);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    adressDifferent(adresse1: AdresseEntite, adresse2: AdresseEntite): boolean {
        if (!adresse1 || !adresse2) return false;
        return adresse1.numero != adresse2.numero || adresse1.rue != adresse2.rue || adresse1.nom != adresse2.nom || adresse1.municipalite != adresse2.municipalite || adresse1.codePostal != adresse2.codePostal;
    }

    printAdresse(adresse: AdresseEntite): string {
        if (!adresse) return '';
        const numero = adresse.numero ? adresse.numero : '';
        const rue = adresse.rue ? adresse.rue : '';
        const municipalite = adresse.municipalite ? adresse.municipalite : '';
        const codePostal = adresse.codePostal ? adresse.codePostal : '';
        // Build full address, omitting blanks and avoiding redundant spaces/commas
        let parts: string[] = [];
        if (numero) parts.push(numero);
        if (rue) parts.push(rue);
        let line = parts.join(' ').trim();

        let cityPart = municipalite ? municipalite : '';
        if (cityPart && codePostal) {
            cityPart += ', ' + codePostal;
        } else if (codePostal) {
            cityPart = codePostal;
        }
        return ((line ? line : '') + (cityPart ? (line ? ' ' : '') + cityPart : '')).trim();
    }

    add(emplacementId: number): void {

    }

    /// <summary>
    /// Opens dialog to add a new address to the emplacement
    /// </summary>
    ouvrirDialogueAjouterAdresse(emplacementId: number) {
        this.dialogService.open(AjouterAdresseEmplacementComponent, {
            header: 'Ajouter une nouvelle adresse',
            width: '600px',
            contentStyle: { 'overflow': 'visible' },
            data: { emplacementId: emplacementId }
        }).onClose.subscribe((nouvelleAdresse: EmplacementAdresseEntite) => {
            if (nouvelleAdresse && nouvelleAdresse.adresse) {
                // Get emplacement info from existing options since backend may not return full emplacement object
                const groupIndex = this.emplacementOptions.findIndex(x => x.value == emplacementId);
                const existingGroup = this.emplacementOptions[groupIndex];
                const principalItem = existingGroup.items.find(x => x['type'] === 'principal');
                const principalAdresse = principalItem?.value as AdresseEntite;

                const newItem = {
                    title: existingGroup.label,
                    label: `${nouvelleAdresse.adresse.nom} ${(this.adressDifferent(principalAdresse, nouvelleAdresse.adresse) ? '(' + this.printAdresse(nouvelleAdresse.adresse) + ')' : '')}`,
                    icon: 'pi pi-map-marker',
                    value: nouvelleAdresse.adresse,
                    type: 'adresse',
                    emplacementId: emplacementId
                } as unknown as SelectItem;

                // Create new array reference to trigger change detection
                const updatedItems = [...existingGroup.items, newItem];
                this.emplacementOptions[groupIndex] = {
                    ...existingGroup,
                    items: updatedItems
                };
                this.emplacementOptions = [...this.emplacementOptions];

                // Use setTimeout to allow dropdown to process the new options before selecting
                setTimeout(() => {
                    // Get the item from the updated options to ensure we use the same reference
                    const addedItem = this.emplacementOptions[groupIndex].items.find(
                        x => x.value?.adresseId === nouvelleAdresse.adresse.adresseId
                    );
                    if (addedItem) {
                        this.selectedAdresse = addedItem.value;
                        this.change(this.selectedAdresse);
                    }
                });

                this.messageService.add({
                    severity: 'success',
                    summary: 'Succès',
                    detail: 'Adresse ajoutée avec succès'
                });
            }
        });
    }
}
